// Cloudinary unsigned upload helper.
//
// This talks to Cloudinary directly from the browser — no backend
// endpoint involved, since Cloudinary's unsigned upload API is built
// exactly for this (that's what "unsigned" means here: no server-side
// signing step required).
//
// One-time setup in your Cloudinary dashboard:
//   1. Settings → Upload → Upload presets → Add upload preset
//   2. Set "Signing Mode" to Unsigned
//   3. Restrict the preset itself — allowed formats (jpg/png/webp),
//      a max file size, and ideally a fixed destination folder (e.g.
//      "raysteghub/admin"). This matters because the preset name isn't
//      a secret: it's visible in any browser's network tab once your
//      site is live. The preset's own restrictions are your actual
//      security boundary, not keeping the name hidden.
//   4. In your .env (same place REACT_APP_API_URL presumably lives):
//        REACT_APP_CLOUDINARY_CLOUD_NAME=your_cloud_name
//        REACT_APP_CLOUDINARY_UPLOAD_PRESET=your_unsigned_preset_name
//      Remember: CRA only reads .env at build/start time — restart your
//      dev server after adding these.
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET

const MAX_FILE_SIZE_MB = 8

/**
 * Upload an image file to Cloudinary and return its hosted URL.
 * Throws on failure — callers should catch and show the message.
 */
export async function uploadImageToCloudinary(file) {
  if (!CLOUD_NAME || !UPLOAD_PRESET) {
    throw new Error(
      'Cloudinary is not configured — set REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET in your .env.'
    )
  }

  if (!file) {
    throw new Error('No file selected.')
  }

  if (!file.type.startsWith('image/')) {
    throw new Error('Please select an image file.')
  }

  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    throw new Error(`Image must be under ${MAX_FILE_SIZE_MB}MB.`)
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('upload_preset', UPLOAD_PRESET)

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: 'POST', body: formData }
  )

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new Error(
      body?.error?.message || 'Upload failed — check your Cloudinary configuration.'
    )
  }

  const data = await res.json()
  return data.secure_url
}